import React, { useMemo } from "react";

const CATS = [
  { key: "compliant",  label: "Compliant",       tone: "text-emerald-700", bar: "bg-emerald-500" },
  { key: "expiring",   label: "Expiring soon",   tone: "text-amber-700",   bar: "bg-amber-500" },
  { key: "expired",    label: "Expired",         tone: "text-red-700",     bar: "bg-red-500" },
  { key: "missing",    label: "Not completed",   tone: "text-slate-700",   bar: "bg-slate-400" },
];

export default function OverviewTab({ summary, actions, onOpenStaff }) {
  const totals = summary || {};
  const bar = useMemo(() => {
    const req = totals.total_requirements || 0;
    if (!req) return { compliant: 0, expiring: 0, expired: 0, missing: 0 };
    return {
      compliant: (totals.compliant_requirements || 0) - (totals.expiring_soon || 0),
      expiring: totals.expiring_soon || 0,
      expired: totals.expired_or_overdue || 0,
      missing: req - (totals.compliant_requirements || 0) - (totals.expired_or_overdue || 0),
    };
  }, [totals]);

  const req = totals.total_requirements || 0;
  const pct = (n) => (req ? (100 * n) / req : 0);

  const attention = [
    ...(actions?.expired || []).map((a) => ({ ...a, kind: "expired" })),
    ...(actions?.expiring || []).map((a) => ({ ...a, kind: "expiring" })),
    ...(actions?.missing || []).map((a) => ({ ...a, kind: "missing" })),
  ].slice(0, 8);

  return (
    <div className="space-y-4" data-testid="training-overview">
      {/* Compliance breakdown card */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-heading text-base font-semibold text-slate-900">Requirement Compliance Breakdown</h2>
            <p className="text-xs text-slate-500 mt-0.5">Live view of assigned training requirements across your staff.</p>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Total Requirements</div>
            <div className="font-heading text-xl font-semibold text-slate-900 tabular-nums">{req}</div>
          </div>
        </div>
        {req > 0 ? (
          <>
            <div className="h-2.5 w-full rounded-full bg-slate-100 overflow-hidden flex" data-testid="overview-bar">
              <div className="bg-emerald-500 h-full" style={{ width: `${pct(bar.compliant)}%` }} />
              <div className="bg-amber-500 h-full"   style={{ width: `${pct(bar.expiring)}%` }} />
              <div className="bg-red-500 h-full"     style={{ width: `${pct(bar.expired)}%` }} />
              <div className="bg-slate-300 h-full"   style={{ width: `${pct(bar.missing)}%` }} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
              {CATS.map((c) => (
                <div key={c.key} className="flex items-start gap-2">
                  <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${c.bar}`} />
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{c.label}</div>
                    <div className={`font-heading text-sm font-semibold tabular-nums ${c.tone}`}>{bar[c.key]}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-sm text-slate-500 py-4">No training requirements assigned yet. Add or activate courses in the <span className="font-medium text-slate-700">Courses</span> tab.</div>
        )}
      </section>

      {/* Latest attention items */}
      <section className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-heading text-base font-semibold text-slate-900">Recent Attention Items</h2>
            <p className="text-xs text-slate-500 mt-0.5">Most pressing expired, expiring or missing mandatory training.</p>
          </div>
          <div className="text-xs text-slate-500 tabular-nums">{actions?.total || 0} total</div>
        </div>
        {attention.length === 0 ? (
          <div className="text-sm text-slate-500 py-8 text-center">Nothing to flag — every requirement is compliant.</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="overview-attention-list">
            {attention.map((a, i) => (
              <li key={i} className="py-2.5 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <button
                    onClick={() => a.user_id && onOpenStaff?.(a.user_id)}
                    className="text-sm font-medium text-slate-900 hover:text-sky-700 truncate max-w-[280px]"
                    data-testid={`attention-open-${a.user_id}-${a.course_id}`}
                  >
                    {a.user_name || "—"}
                  </button>
                  <div className="text-xs text-slate-500 truncate">{a.course_name} · {a.category || "—"}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {a.expiry_date && <span className="text-[11px] text-slate-500 tabular-nums">{a.expiry_date}</span>}
                  <StatusChip kind={a.kind} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatusChip({ kind }) {
  const map = {
    expired:  { label: "Expired",  bg: "bg-red-100",     text: "text-red-800",     border: "border-red-200" },
    expiring: { label: "Expiring", bg: "bg-amber-100",   text: "text-amber-900",   border: "border-amber-300" },
    missing:  { label: "Not Completed", bg: "bg-slate-100", text: "text-slate-700", border: "border-slate-200" },
  };
  const s = map[kind] || map.missing;
  return <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded-full border ${s.bg} ${s.text} ${s.border}`}>{s.label}</span>;
}
